import React, { useState, useEffect } from 'react';
import { staffClient } from 'clients/staff';
import Table, { Column } from 'ui/Table';
import Loading from 'ui/Loading';
import FilterCard, { FilterCardGroup } from 'ui/FilterCard';
import Section from 'ui/Section';
import { Badge, Switch } from 'ui/shadcn';
import type { BadgeProps } from 'ui/shadcn';
import { toast } from 'react-toastify';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { ToggleGroup } from 'ui/form';
import { SITE_CATEGORIES, SITE_CATEGORY_BADGE, SITE_CATEGORY_LABEL, SiteCategory } from 'apis/site';

interface SiteItem {
    id: number;
    name: string;
    type: string;
    location?: string;
    constructionStartDate?: string;
    constructionEndDate?: string;
    totalFloorArea?: string;
    buildingScale?: string;
    numberOfUnits?: string;
    category?: SiteCategory;
    visible?: boolean;
    isFeatured?: boolean;
    featuredOrder?: number;
}

type FilterType = 'ALL' | 'FEATURED' | 'NOT_FEATURED';
type CategoryFilter = 'ALL' | SiteCategory;

const CATEGORY_OPTIONS = [
    { value: 'ALL', label: '전체' },
    ...SITE_CATEGORIES.map((c) => ({ value: c, label: SITE_CATEGORY_LABEL[c] })),
];

const STATUS_LABEL: Record<string, { label: string; variant: BadgeProps['variant'] }> = {
    'IN_PROGRESS': { label: '진행중', variant: 'default' },
    'CONTRACT_COMPLETED': { label: '계약완료', variant: 'warning' },
    'COMPLETED': { label: '준공', variant: 'secondary' },
    'ETC': { label: '기타', variant: 'outline' },
};

const PortfolioManager: React.FC = () => {
    const [sites, setSites] = useState<SiteItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<FilterType>('ALL');
    const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('ALL');

    useEffect(() => { fetchSites(); }, []);

    const fetchSites = async () => {
        try {
            const res = await staffClient.get('/site', { params: { limit: 1000 } });
            const all = (res.data?.data || []).filter((s: any) => s.visible !== false);
            setSites(all);
        } catch (error) {
            toast.error('현장 목록을 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleFeatured = async (site: SiteItem) => {
        try {
            const next = !site.isFeatured;
            const featuredOrder = next
                ? (Math.max(0, ...sites.filter(s => s.isFeatured).map(s => s.featuredOrder || 0)) + 1)
                : 0;
            await staffClient.patch(`/site/${site.id}`, { isFeatured: next, featuredOrder });
            setSites(prev => prev.map(s => s.id === site.id ? { ...s, isFeatured: next, featuredOrder } : s));
            toast.success(next ? '주요 프로젝트로 노출됩니다.' : '주요 프로젝트에서 제외했습니다.');
        } catch (error) {
            toast.error('변경에 실패했습니다.');
        }
    };

    const moveOrder = async (site: SiteItem, direction: -1 | 1) => {
        const featured = sites.filter(s => s.isFeatured)
            .sort((a, b) => (a.featuredOrder || 0) - (b.featuredOrder || 0));
        const idx = featured.findIndex(s => s.id === site.id);
        const swapIdx = idx + direction;
        if (swapIdx < 0 || swapIdx >= featured.length) return;
        const target = featured[swapIdx];

        try {
            const myOrder = site.featuredOrder ?? 0;
            const targetOrder = target.featuredOrder ?? 0;
            await Promise.all([
                staffClient.patch(`/site/${site.id}`, { featuredOrder: targetOrder }),
                staffClient.patch(`/site/${target.id}`, { featuredOrder: myOrder }),
            ]);
            setSites(prev => prev.map(s => {
                if (s.id === site.id) return { ...s, featuredOrder: targetOrder };
                if (s.id === target.id) return { ...s, featuredOrder: myOrder };
                return s;
            }));
        } catch {
            toast.error('순서 변경에 실패했습니다.');
        }
    };

    const formatPeriod = (start?: string, end?: string) => {
        const fmt = (d?: string) => {
            if (!d) return '';
            const date = new Date(d);
            return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}`;
        };
        if (!start && !end) return '-';
        return `${fmt(start)} ~ ${fmt(end)}`;
    };

    const inCategory = categoryFilter === 'ALL'
        ? sites
        : sites.filter(s => (s.category || 'SUPERVISION') === categoryFilter);
    const featuredCount = inCategory.filter(s => s.isFeatured).length;

    let filtered = inCategory;
    if (filter === 'FEATURED') filtered = inCategory.filter(s => s.isFeatured);
    if (filter === 'NOT_FEATURED') filtered = inCategory.filter(s => !s.isFeatured);

    if (filter === 'FEATURED') {
        filtered = [...filtered].sort((a, b) => (a.featuredOrder || 0) - (b.featuredOrder || 0));
    }

    const columns: Column<SiteItem>[] = [
        {
            key: 'isFeatured',
            header: '노출',
            width: '70px',
            align: 'center',
            render: (value, row) => (
                <Switch
                    checked={!!value}
                    onClick={(e) => e.stopPropagation()}
                    onCheckedChange={() => handleToggleFeatured(row)}
                    title={value ? '주요 프로젝트로 노출중' : '비노출'}
                    aria-label="주요 프로젝트 노출"
                />
            ),
        },
        {
            key: 'category',
            header: '구분',
            width: '80px',
            align: 'center',
            render: (value) => {
                const category = (value || 'SUPERVISION') as SiteCategory;
                const badge = SITE_CATEGORY_BADGE[category];
                return <Badge variant={badge.variant}>{badge.label}</Badge>;
            },
        },
        {
            key: 'type',
            header: '상태',
            width: '90px',
            align: 'center',
            render: (value) => {
                const conf = STATUS_LABEL[value] || STATUS_LABEL['ETC'];
                return <Badge variant={conf.variant}>{conf.label}</Badge>;
            },
        },
        {
            key: 'name',
            header: '현장명',
            render: (value) => <span className="font-medium">{value}</span>,
        },
        {
            key: 'location',
            header: '위치',
            width: '200px',
            render: (value) => <span className="text-muted-foreground">{value || '-'}</span>,
        },
        {
            key: 'numberOfUnits',
            header: '세대수',
            width: '90px',
            align: 'right',
            render: (value) => <span className="tabular-nums">{value || '-'}</span>,
        },
        {
            key: 'totalFloorArea',
            header: '연면적',
            width: '120px',
            align: 'right',
            render: (value) => <span className="tabular-nums">{value || '-'}</span>,
        },
        {
            key: 'constructionStartDate',
            header: '공사기간',
            width: '160px',
            align: 'center',
            render: (_, row) => <span className="tabular-nums text-muted-foreground">{formatPeriod(row.constructionStartDate, row.constructionEndDate)}</span>,
        },
    ];

    if (filter === 'FEATURED') {
        columns.unshift({
            key: 'featuredOrder',
            header: '순서',
            width: '90px',
            align: 'center',
            render: (_, row) => (
                <div className="inline-flex items-center gap-1">
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); moveOrder(row, -1); }}
                        className="inline-flex h-7 w-7 items-center justify-center rounded border border-input bg-background text-neutral-700 hover:bg-muted"
                        title="위로"
                    ><ChevronUp className="h-3.5 w-3.5" /></button>
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); moveOrder(row, 1); }}
                        className="inline-flex h-7 w-7 items-center justify-center rounded border border-input bg-background text-neutral-700 hover:bg-muted"
                        title="아래로"
                    ><ChevronDown className="h-3.5 w-3.5" /></button>
                </div>
            ),
        });
    }

    return (
        <div>
            <div className="mb-4">
                <ToggleGroup
                    label="구분"
                    options={CATEGORY_OPTIONS}
                    value={categoryFilter}
                    onChange={(next) => setCategoryFilter((next || 'ALL') as CategoryFilter)}
                />
            </div>

            <FilterCardGroup columns={3}>
                <FilterCard label="전체 현장" count={loading ? undefined : inCategory.length} suffix="건" active={filter === 'ALL'} onClick={() => setFilter('ALL')} />
                <FilterCard label="주요 프로젝트 노출중" count={loading ? undefined : featuredCount} suffix="건" active={filter === 'FEATURED'} onClick={() => setFilter('FEATURED')} />
                <FilterCard label="비노출" count={loading ? undefined : inCategory.length - featuredCount} suffix="건" active={filter === 'NOT_FEATURED'} onClick={() => setFilter('NOT_FEATURED')} />
            </FilterCardGroup>

            <Section
                className="overflow-hidden"
                title={
                    <>
                        현장 목록
                        {!loading && (
                            <span className="text-xs font-normal text-muted-foreground tabular-nums">
                                총 <span className="font-medium text-foreground">{filtered.length}</span>건
                            </span>
                        )}
                    </>
                }
                description="홈페이지 수행실적 > 주요 프로젝트에 노출될 현장을 관리합니다. 감리·공사·설계 실적 목록은 현장 구분에 따라 홈페이지에 자동으로 나갑니다. 현장 정보 수정은 현장 관리에서 합니다."
                flush
            >
                {loading ? (
                    <Loading text="현장을 불러오는 중..." />
                ) : (
                    <Table
                        columns={columns}
                        data={filtered}
                        pagination
                        pageSize={15}
                        emptyMessage={filter === 'FEATURED' ? '노출중인 주요 프로젝트가 없습니다.' : '현장이 없습니다.'}
                    />
                )}
            </Section>
        </div>
    );
};

export default PortfolioManager;
